import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatEUR, todayISO, DEFAULT_VAT_RATE } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { ArrowLeft, Plus, Trash2, Save, Send, Copy, XCircle, Archive, Eye, Layers, ChevronDown, ChevronUp, Package, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { PdfPreviewDialog } from "@/components/PdfPreview";
import { GoogleActions } from "@/components/GoogleActions";
import { StorageSection } from "@/components/quote/StorageSection";
import { RelatedDocsList } from "@/components/quote/RelatedDocsList";
import { useProformaCalculations } from "@/lib/useDocCalculations";
import { useLocalDraft } from "@/lib/useLocalDraft";
import { LocalDraftBanner, LocalDraftSavedIndicator } from "@/components/LocalDraftBanner";
import { VatModeSelector } from "@/components/VatModeSelector";
import { VAT_MODE_STANDARD, VAT_MODE_SHORT } from "@/lib/vatModes";
import { PickupPickerDialog } from "@/components/inventory/PickupPickerDialog";

const emptyLine = () => ({ id: "", description: "", quantity: 1, unit_price: 0, sort_order: 0 });
const emptyExtra = () => ({ description: "", amount: 0 });
const emptyMultiItem = () => ({ description: "", price: 0, sub_items: [{ description: "", quantity: 1 }] });

export default function ProformaForm() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isNew = !id;
  const canEditAll = hasPermission("proformas.edit_all");
  const canIssue = hasPermission("proformas.issue");

  const [form, setForm] = useState({
    header_template_id: "", customer_id: "", date: todayISO(), subject: "",
    notes: "", pricing_type: "itemized", lump_sum_amount: 0,
    extra_lump_sum_amount: 0, extra_lump_sum_label: "Quotazione forfettaria",
    extra_lump_sum_items: [],
    delivery_type: "consegna",
    line_items: [emptyLine()], multi_items: [], extra_costs: [],
    deposit: 0, vat_rate: DEFAULT_VAT_RATE, insurance_requested: false, insurance_amount: 0,
    vat_mode: VAT_MODE_STANDARD, vat_legal_text: "",
    materials_cost: 0,
    discount_amount: 0,
    storage_handling_cost: 0, storage_monthly_cost: 0,
    storage_months: 0, storage_free_months: 0,
    storage_release_type: "", storage_release_price: 0,
    payment_notes: "", payment_due_date: "", payment_bank_account: "",
    proforma_type: "standard", linked_proforma_id: null,
    extension_decimal_months: null, extension_residual_days: null, extension_source_proforma_id: null
  });
  const [pf, setPf] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [relatedDocs, setRelatedDocs] = useState({ inventories: [], proformas: [] });
  const [pickupPicker, setPickupPicker] = useState({ open: false, items: [] });

  const loadData = useCallback(async () => {
    try {
      const [custRes, tmplRes, orgRes] = await Promise.all([
        api.get("/customers?limit=200"),
        api.get("/header-templates"),
        api.get("/organization"),
      ]);
      setCustomers(custRes.data.items || []);
      setTemplates(tmplRes.data || []);
      setBankAccounts(orgRes.data?.bank_accounts || []);
      if (id) {
        const { data } = await api.get(`/proformas/${id}`);
        setPf(data);
        setForm({
          header_template_id: data.header_template_id || "",
          customer_id: data.customer_id || "",
          date: data.date || todayISO(),
          subject: data.subject || "",
          notes: data.notes || "",
          pricing_type: data.pricing_type || "itemized",
          lump_sum_amount: data.lump_sum_amount || 0,
          extra_lump_sum_amount: data.extra_lump_sum_amount || 0,
          extra_lump_sum_label: data.extra_lump_sum_label || "Quotazione forfettaria",
          extra_lump_sum_items: data.extra_lump_sum_items || [],
          delivery_type: data.delivery_type || "consegna",
          line_items: data.line_items?.length ? data.line_items : [emptyLine()],
          multi_items: data.multi_items || [],
          extra_costs: data.extra_costs || [],
          deposit: data.deposit || 0,
          vat_rate: data.vat_rate ?? DEFAULT_VAT_RATE,
          vat_mode: data.vat_mode || VAT_MODE_STANDARD,
          vat_legal_text: data.vat_legal_text || "",
          insurance_requested: data.insurance_requested || false,
          insurance_amount: data.insurance_amount || 0,
          materials_cost: data.materials_cost || 0,
          discount_amount: data.discount_amount || 0,
          storage_handling_cost: data.storage_handling_cost || 0,
          storage_monthly_cost: data.storage_monthly_cost || 0,
          storage_months: data.storage_months || 0,
          storage_free_months: data.storage_free_months || 0,
          storage_release_type: data.storage_release_type || "",
          storage_release_price: data.storage_release_price || 0,
          payment_notes: data.payment_notes || "",
          payment_due_date: data.payment_due_date || "",
          payment_bank_account: data.payment_bank_account || "",
          proforma_type: data.proforma_type || "standard",
          linked_proforma_id: data.linked_proforma_id || null,
          extension_decimal_months: data.extension_decimal_months || null,
          extension_residual_days: data.extension_residual_days || null,
          extension_source_proforma_id: data.extension_source_proforma_id || null
        });
        if (data.quote_id) {
          api.get(`/quotes/${data.quote_id}/related-docs`)
            .then(r => setRelatedDocs({ inventories: r.data.inventories || [], proformas: r.data.proformas || [] }))
            .catch(() => {});
        }
      }
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const isDraft = !pf || pf.status === "DRAFT";
  const isEditable = isDraft && canEditAll;

  // ---- Persistent local draft (offline / network-loss recovery) ----
  const draftKey = isNew ? "proforma:new" : (id ? `proforma:${id}` : null);
  const draft = useLocalDraft({
    key: draftKey,
    form,
    enabled: isEditable && !loading,
    serverUpdatedAt: pf?.updated_at || null,
  });
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!draft.savedAt) return;
    const t = setInterval(() => forceTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, [draft.savedAt]);
  const handleRestoreDraft = () => {
    const data = draft.restore();
    if (data) {
      setForm(data);
      toast.success("Bozza locale ripristinata");
    }
  };

  const updateLine = (idx, field, val) => { const items = [...form.line_items]; items[idx] = { ...items[idx], [field]: val }; setForm({ ...form, line_items: items }); };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
  const addExtra = () => setForm({ ...form, extra_costs: [...form.extra_costs, emptyExtra()] });
  const removeExtra = (idx) => setForm({ ...form, extra_costs: form.extra_costs.filter((_, i) => i !== idx) });
  const updateExtra = (idx, field, val) => { const costs = [...form.extra_costs]; costs[idx] = { ...costs[idx], [field]: val }; setForm({ ...form, extra_costs: costs }); };

  // Multi items helpers
  const addMultiItem = () => setForm({ ...form, multi_items: [...form.multi_items, emptyMultiItem()] });
  const removeMultiItem = (idx) => setForm({ ...form, multi_items: form.multi_items.filter((_, i) => i !== idx) });
  const updateMultiItem = (idx, field, val) => { const items = [...form.multi_items]; items[idx] = { ...items[idx], [field]: val }; setForm({ ...form, multi_items: items }); };
  const addSubItem = (miIdx) => { const items = [...form.multi_items]; items[miIdx] = { ...items[miIdx], sub_items: [...items[miIdx].sub_items, { description: "", quantity: 1 }] }; setForm({ ...form, multi_items: items }); };
  const removeSubItem = (miIdx, siIdx) => { const items = [...form.multi_items]; items[miIdx] = { ...items[miIdx], sub_items: items[miIdx].sub_items.filter((_, i) => i !== siIdx) }; setForm({ ...form, multi_items: items }); };
  const updateSubItem = (miIdx, siIdx, field, val) => { const items = [...form.multi_items]; const subs = [...items[miIdx].sub_items]; subs[siIdx] = { ...subs[siIdx], [field]: val }; items[miIdx] = { ...items[miIdx], sub_items: subs }; setForm({ ...form, multi_items: items }); };

  // Calcoli derivati: tutti memoizzati nell'hook useProformaCalculations
  const calc = useProformaCalculations(form);
  const {
    isLumpSum, lineSubtotal, multiSubtotal, extraLumpSum, isDeposito,
    subtotal, extraTotal, paidMonths, depositCost, fractionalDeposit, releaseCost,
    storageTotal, materialsCost, totalBeforeVat, vatAmount, insuranceAmount,
    totalWithVat, discountAmount, total, balanceDue, extDecimal,
    vatMode, vatZeroed,
    visibleMultiItems, visibleExtraCosts, positiveExtraCount,
  } = calc;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, line_items: form.line_items.map((li, i) => ({ ...li, sort_order: i })), deposit: parseFloat(form.deposit) || 0, vat_rate: parseFloat(form.vat_rate) || DEFAULT_VAT_RATE };
      if (isNew) {
        const { data } = await api.post("/proformas", payload);
        toast.success("Fattura proforma creata");
        draft.clear(form);
        navigate(`/proformas/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/proformas/${id}`, payload);
        setPf(data);
        toast.success("Fattura proforma aggiornata");
        draft.clear(form);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action) => {
    try {
      if (action === "issue") { const { data } = await api.post(`/proformas/${id}/issue`); setPf(data); toast.success("Fattura proforma emessa"); }
      else if (action === "version") { const { data } = await api.post(`/proformas/${id}/version`); toast.success("Nuova versione creata"); navigate(`/proformas/${data.id}`, { replace: true }); }
      else if (action === "cancel") { await api.post(`/proformas/${id}/cancel`); toast.success("Annullata"); loadData(); }
      else if (action === "archive") { await api.post(`/proformas/${id}/archive`); toast.success("Archiviata"); loadData(); }
      else if (action === "delete") { if (!window.confirm("Eliminare?")) return; await api.delete(`/proformas/${id}`); toast.success("Eliminata"); navigate("/proformas"); }
      else if (action === "pdf") {
        setPdfPreviewUrl(`/proformas/${id}/pdf`);
      }
      else if (action === "inventory") {
        const { data } = await api.post(`/inventories/from-quote/${pf.quote_id}`);
        toast.success("Inventario creato");
        navigate(`/inventories/${data.id}`);
      }
      else if (action === "release_inventory") {
        // 1) chiediamo al backend la lista dei pickup eligibili (= senza release DRAFT)
        const { data: list } = await api.get(`/inventories/eligible-pickups-for-release/${pf.quote_id}`);
        const eligibles = list?.items || [];
        if (eligibles.length === 0) {
          // Nessun pickup → fallback agli items del preventivo via legacy endpoint
          const { data } = await api.post(`/inventories/release-from-quote/${pf.quote_id}`);
          toast.success("Inventario di Riconsegna creato");
          navigate(`/inventories/${data.id}`);
        } else if (eligibles.length === 1) {
          // Un solo pickup → vai diretto
          const { data } = await api.post(`/inventories/release-from-inventory/${eligibles[0].id}`);
          toast.success("Inventario di Riconsegna creato");
          navigate(`/inventories/${data.id}`);
        } else {
          // ≥ 2 pickup → apri dialog di scelta
          setPickupPicker({ open: true, items: eligibles });
        }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="proforma-form-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/proformas")} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold tracking-tighter font-['Outfit']">{isNew ? "Nuova Fattura Proforma" : (pf?.number || "Proforma Bozza")}</h1>
              {pf?.proforma_type === "storage_deposit" && (
                <span data-testid="pf-type-badge-deposit" className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm bg-blue-50 text-[#002FA7] border border-[#002FA7]/30">Deposito</span>
              )}
              {pf?.proforma_type === "storage_release" && (
                <span data-testid="pf-type-badge-release" className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Riconsegna</span>
              )}
            </div>
            {pf && <StatusBadge status={pf.status} />}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isEditable && <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="save-proforma-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva"}</Button>}
          {!isNew && canIssue && isDraft && <Button onClick={() => handleAction("issue")} variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="issue-proforma-btn"><Send className="w-4 h-4 mr-1" /> Emetti</Button>}
          {!isNew && canEditAll && pf?.status === "ISSUED" && <Button onClick={() => handleAction("version")} variant="outline" className="rounded-sm" data-testid="version-proforma-btn"><Copy className="w-4 h-4 mr-1" /> Aggiorna ID</Button>}
          {!isNew && pf?.quote_id && hasPermission("inventories.create") && pf?.proforma_type !== "storage_release" && pf?.proforma_type !== "storage_extension" && (() => {
            const linked = relatedDocs.inventories.filter(i => i.inventory_type !== "release");
            const last = linked[linked.length - 1];
            // Mostra "Crea" solo se NON esiste già un inventario di ritiro modificabile (DRAFT).
            const hasEditablePickup = linked.some(i => i.status === "DRAFT");
            return (
              <>
                {last && (
                  <button
                    onClick={() => navigate(`/inventories/${last.id}`)}
                    data-testid="linked-inventory-chip"
                    title="Apri l'inventario collegato"
                    className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-sm border border-[#002FA7]/30 bg-blue-50/60 hover:bg-blue-100/70 transition text-xs"
                  >
                    <Package className="w-3.5 h-3.5 text-[#002FA7]" />
                    <span className="text-zinc-700">{linked.length === 1 ? "Inventario collegato" : `Inventari collegati: ${linked.length}`}</span>
                    <StatusBadge status={last.status} />
                  </button>
                )}
                {!hasEditablePickup && (
                  <Button onClick={() => handleAction("inventory")} variant="outline" className="rounded-sm" data-testid="create-inventory-from-pf-btn"><Package className="w-4 h-4 mr-1" /> Crea Inventario</Button>
                )}
              </>
            );
          })()}
          {!isNew && pf?.quote_id && hasPermission("inventories.create") && (pf?.proforma_type === "storage_release" || pf?.proforma_type === "storage_extension") && (() => {
            const linked = relatedDocs.inventories.filter(i => i.inventory_type === "release");
            const last = linked[linked.length - 1];
            // Mostra "Crea" solo se NON esiste già un inventario di riconsegna modificabile (DRAFT).
            // Se tutti sono congelati (Emessi/Annullati/Archiviati), il bottone resta visibile per
            // permettere di crearne uno nuovo per la prossima sessione operativa.
            const hasEditableRelease = linked.some(i => i.status === "DRAFT");
            return (
              <>
                {last && (
                  <button
                    onClick={() => navigate(`/inventories/${last.id}`)}
                    data-testid="linked-release-inventory-chip"
                    title="Apri l'inventario di Riconsegna collegato"
                    className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-sm border border-amber-300 bg-amber-50/60 hover:bg-amber-100/70 transition text-xs"
                  >
                    <PackageCheck className="w-3.5 h-3.5 text-amber-700" />
                    <span className="text-zinc-700">{linked.length === 1 ? "Inventario Riconsegna collegato" : `Inventari Riconsegna collegati: ${linked.length}`}</span>
                    <StatusBadge status={last.status} />
                  </button>
                )}
                {!hasEditableRelease && (
                  <Button onClick={() => handleAction("release_inventory")} variant="outline" className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="create-release-inventory-from-pf-btn"><PackageCheck className="w-4 h-4 mr-1" /> Crea Inventario Riconsegna</Button>
                )}
              </>
            );
          })()}
          {!isNew && <Button onClick={() => handleAction("pdf")} variant="outline" className="rounded-sm" data-testid="pdf-proforma-btn"><Eye className="w-4 h-4 mr-1" /> Anteprima PDF</Button>}
          {!isNew && hasPermission("google.drive") && <GoogleActions docType="proforma" docId={id} docNumber={pf?.number} customerEmail={pf?.customer_email} />}
          {!isNew && canEditAll && pf?.status !== "CANCELLED" && pf?.status !== "ARCHIVED" && (
            <>
              <Button onClick={() => handleAction("cancel")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50"><XCircle className="w-4 h-4 mr-1" /> Annulla</Button>
              <Button onClick={() => handleAction("archive")} variant="outline" className="rounded-sm"><Archive className="w-4 h-4 mr-1" /> Archivia</Button>
            </>
          )}
          {!isNew && canEditAll && isDraft && <Button onClick={() => handleAction("delete")} variant="outline" className="rounded-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>}
        </div>
      </div>

      {isEditable && <LocalDraftBanner draft={draft} onRestore={handleRestoreDraft} kind="pf" />}
      {isEditable && <LocalDraftSavedIndicator draft={draft} kind="pf" />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {pf?.proforma_type === "storage_deposit" && (
            <div data-testid="pf-deposit-banner" className="rounded-sm border border-[#002FA7]/30 bg-blue-50/50 p-4 text-sm text-zinc-700 flex items-start gap-3">
              <div className="text-[#002FA7] font-semibold uppercase text-[10px] tracking-wider shrink-0 mt-0.5">Deposito</div>
              <div className="space-y-1">
                <div>Questa proforma include il <strong>canone mensile di deposito</strong> e gli altri servizi del preventivo. <strong>Costo Movimentazione</strong> e <strong>Prezzo Riconsegna</strong> sono fatturati separatamente alla data di riconsegna.</div>
                {pf?.linked_proforma_id && (
                  <button onClick={() => navigate(`/proformas/${pf.linked_proforma_id}`)} className="text-[#002FA7] hover:underline font-medium" data-testid="pf-linked-link">
                    → Vai alla proforma di Riconsegna collegata
                  </button>
                )}
              </div>
            </div>
          )}
          {pf?.proforma_type === "storage_release" && (
            <div data-testid="pf-release-banner" className="rounded-sm border border-amber-300 bg-amber-50/50 p-4 text-sm text-zinc-700 flex items-start gap-3">
              <div className="text-amber-700 font-semibold uppercase text-[10px] tracking-wider shrink-0 mt-0.5">Riconsegna</div>
              <div className="space-y-1">
                <div>Questa proforma include esclusivamente <strong>Costo Movimentazione</strong> e <strong>Prezzo Riconsegna</strong>. Da emettere alla data effettiva di riconsegna dei beni dal deposito{pf?.payment_due_date ? ` (data stimata: ${pf.payment_due_date.split("-").reverse().join("/")})` : ""}.</div>
                {pf?.linked_proforma_id && (
                  <button onClick={() => navigate(`/proformas/${pf.linked_proforma_id}`)} className="text-amber-700 hover:underline font-medium" data-testid="pf-linked-link">
                    → Vai alla proforma di Deposito collegata
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="card-surface p-5 space-y-4">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Informazioni</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1"><Label className="text-xs text-zinc-500">Template</Label>
                <Select value={form.header_template_id} onValueChange={(v) => setForm({ ...form, header_template_id: v })} disabled={!isEditable}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs text-zinc-500">Cliente *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={!isEditable}>
                  <SelectTrigger data-testid="pf-customer-select" className="rounded-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs text-zinc-500">Data</Label><DateInput value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!isEditable} className="rounded-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-zinc-500">Oggetto</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} disabled={!isEditable} className="rounded-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-zinc-500">Scadenza Pagamento</Label><DateInput value={form.payment_due_date} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} disabled={!isEditable} className="rounded-sm" /></div>
            </div>
          </div>

          {/* Quotazione */}
          <div className="card-surface p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Quotazione</h2>
              {isEditable && (
                <div className="flex items-center gap-1 bg-zinc-100 rounded-sm p-0.5">
                  <button onClick={() => setForm({ ...form, pricing_type: "itemized" })}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${!isLumpSum ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
                    A Voci
                  </button>
                  <button onClick={() => setForm({ ...form, pricing_type: "lump_sum" })}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${isLumpSum ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
                    A Corpo
                  </button>
                </div>
              )}
            </div>

            {isLumpSum && (
              <div className="space-y-2">
                <Label className="text-xs text-zinc-500">Importo Forfettario</Label>
                <Input type="number" min="0" step="0.01" placeholder="Importo a corpo (€)" value={form.lump_sum_amount}
                  onChange={(e) => setForm({ ...form, lump_sum_amount: parseFloat(e.target.value) || 0 })} disabled={!isEditable} className="rounded-sm text-lg font-semibold max-w-xs" />
              </div>
            )}

            {/* Line Items */}
            <div className={isLumpSum ? "border-t border-zinc-200 pt-4 mt-4" : ""}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400 font-medium">Elementi Singoli — {form.line_items.length} {form.line_items.length === 1 ? "voce" : "voci"}</span>
                {isEditable && <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm h-8"><Plus className="w-3 h-3 mr-1" /> Aggiungi</Button>}
              </div>
              <div className="space-y-2 mt-3">
                {form.line_items.map((li, idx) => (
                  <div key={li.id || idx} className="flex gap-2 items-start">
                    <Input placeholder="Descrizione" value={li.description} onChange={(e) => updateLine(idx, "description", e.target.value)} disabled={!isEditable} className="rounded-sm flex-1" />
                    <Input type="number" placeholder="Qtà" value={li.quantity} onChange={(e) => updateLine(idx, "quantity", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-20" />
                    {!isLumpSum && <>
                      <Input type="number" placeholder="Prezzo (€)" value={li.unit_price} onChange={(e) => updateLine(idx, "unit_price", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-28" />
                      <span className="text-sm text-zinc-500 w-24 text-right pt-2 whitespace-nowrap">{formatEUR((li.quantity || 0) * (li.unit_price || 0))}</span>
                    </>}
                    {isEditable && form.line_items.length > 1 && <button onClick={() => removeLine(idx)} className="p-2 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>}
                  </div>
                ))}
              </div>
            </div>

            {/* Multi Items */}
            <div className="border-t border-zinc-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                  <Layers className="w-4 h-4" /> Elementi Multipli — {(form.multi_items || []).length} grupp{(form.multi_items || []).length === 1 ? "o" : "i"}
                </span>
                {isEditable && <Button size="sm" variant="outline" onClick={addMultiItem} className="rounded-sm h-8"><Plus className="w-3 h-3 mr-1" /> Elemento Multiplo</Button>}
              </div>
              <div className="space-y-3 mt-3">
                {(form.multi_items || []).map((mi, miIdx) => (
                  <PfMultiItemCard key={`pf-mi-${miIdx}`} mi={mi} miIdx={miIdx} isEditable={isEditable} hidePrice={isLumpSum}
                    updateMultiItem={updateMultiItem} removeMultiItem={removeMultiItem}
                    addSubItem={addSubItem} removeSubItem={removeSubItem} updateSubItem={updateSubItem} />
                ))}
                {(form.multi_items || []).length === 0 && <p className="text-xs text-zinc-400">Nessun elemento multiplo</p>}
              </div>
            </div>

            {/* Forfait aggiuntivo — solo con itemized + delivery=deposito */}
            {!isLumpSum && form.delivery_type === "deposito" && (
              <div className="border-t border-zinc-200 pt-4 mt-4 space-y-3" data-testid="pf-extra-lump-sum-section">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full" /> Quotazione forfettaria aggiuntiva
                  </span>
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Solo con Scarico Deposito</span>
                </div>

                <div className="space-y-2" data-testid="pf-extra-lump-sum-items-list">
                  {(form.extra_lump_sum_items || []).map((it, i) => (
                    <div key={`pf-extra-row-${i}`} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center" data-testid={`pf-extra-lump-sum-item-${i}`}>
                      <Input
                        data-testid={`pf-extra-lump-sum-item-${i}-desc`}
                        placeholder="Descrizione"
                        value={it.description || ""}
                        onChange={(e) => {
                          const next = [...form.extra_lump_sum_items];
                          next[i] = { ...next[i], description: e.target.value };
                          setForm({ ...form, extra_lump_sum_items: next });
                        }}
                        disabled={!isEditable}
                        className="rounded-sm"
                      />
                      <Input
                        data-testid={`pf-extra-lump-sum-item-${i}-qty`}
                        type="number" inputMode="decimal" min="0" step="0.01"
                        placeholder="Qtà"
                        value={it.quantity ?? 1}
                        onChange={(e) => {
                          const next = [...form.extra_lump_sum_items];
                          next[i] = { ...next[i], quantity: parseFloat(e.target.value) || 0 };
                          setForm({ ...form, extra_lump_sum_items: next });
                        }}
                        disabled={!isEditable}
                        className="rounded-sm w-24 text-right"
                      />
                      {isEditable && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = form.extra_lump_sum_items.filter((_, idx) => idx !== i);
                            setForm({ ...form, extra_lump_sum_items: next });
                          }}
                          data-testid={`pf-extra-lump-sum-item-${i}-delete`}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-sm"
                          aria-label="Rimuovi riga"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {isEditable && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const next = [...(form.extra_lump_sum_items || []), { description: "", quantity: 1 }];
                        setForm({ ...form, extra_lump_sum_items: next });
                      }}
                      className="rounded-sm h-8"
                      data-testid="pf-add-extra-lump-sum-item-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  )}
                </div>

                {/* Importo forfettario — separato */}
                <div className="bg-amber-50/40 border border-amber-200/60 rounded-sm p-3">
                  <Label htmlFor="pf-extra-lump-amount" className="text-xs text-zinc-700 font-semibold uppercase tracking-wider">
                    Importo Forfettario (€)
                  </Label>
                  <Input
                    id="pf-extra-lump-amount"
                    data-testid="pf-extra-lump-sum-amount"
                    type="number" inputMode="decimal" min="0" step="0.01"
                    placeholder="0,00"
                    value={form.extra_lump_sum_amount || 0}
                    onChange={(e) => setForm({ ...form, extra_lump_sum_amount: parseFloat(e.target.value) || 0 })}
                    disabled={!isEditable}
                    className="rounded-sm w-40 mt-1.5 font-semibold text-lg"
                  />
                  <input
                    type="hidden"
                    data-testid="pf-extra-lump-sum-label"
                    value={form.extra_lump_sum_label || "Quotazione forfettaria"}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Deposito Magazzino */}
          <StorageSection form={form} setForm={setForm} isEditable={isEditable} storageTotal={storageTotal} paidMonths={paidMonths} />

          {/* Extra costs */}
          <div className="card-surface p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Costi Extra</h2>
              {isEditable && <Button size="sm" variant="outline" onClick={addExtra} className="rounded-sm h-8"><Plus className="w-3 h-3 mr-1" /> Aggiungi</Button>}
            </div>
            {form.extra_costs.map((ec, idx) => (
              <div key={ec.id || `extra-${idx}`} className="flex gap-2 items-center">
                <Input placeholder="Descrizione" value={ec.description} onChange={(e) => updateExtra(idx, "description", e.target.value)} disabled={!isEditable} className="rounded-sm flex-1" />
                <Input type="number" placeholder="Importo (€)" value={ec.amount} onChange={(e) => updateExtra(idx, "amount", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-32" />
                {isEditable && <button onClick={() => removeExtra(idx)} className="p-2 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>}
              </div>
            ))}
          </div>

          {/* Assicurazione (esente IVA) */}
          <div className="card-surface p-5 space-y-3">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Assicurazione</h2>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pf-insurance-requested" checked={form.insurance_requested} onChange={(e) => setForm({ ...form, insurance_requested: e.target.checked })} disabled={!isEditable} className="rounded-sm" />
              Includi assicurazione <span className="text-zinc-400 text-xs">(costo esente IVA)</span>
            </label>
            {form.insurance_requested && (
              <Input data-testid="pf-insurance-amount" type="number" min="0" step="0.01" placeholder="Importo assicurazione €"
                value={form.insurance_amount} onChange={(e) => setForm({ ...form, insurance_amount: parseFloat(e.target.value) || 0 })}
                disabled={!isEditable} className="rounded-sm max-w-xs" />
            )}
          </div>

          <VatModeSelector
            form={form}
            onChange={({ vat_mode, vat_legal_text }) =>
              setForm({ ...form, vat_mode, vat_legal_text })
            }
            disabled={!isEditable}
            testIdPrefix="pf"
          />

          <div className="card-surface p-5 space-y-4">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Note e Pagamento</h2>
            <textarea placeholder="Note..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!isEditable} className="input-field min-h-[80px] resize-y" rows={3} />
            <textarea placeholder="Modalità di pagamento..." value={form.payment_notes} onChange={(e) => setForm({ ...form, payment_notes: e.target.value })} disabled={!isEditable} className="input-field min-h-[60px] resize-y" rows={2} />
            {bankAccounts.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500">Conto corrente per bonifico</Label>
                <Select value={form.payment_bank_account} onValueChange={(v) => setForm({ ...form, payment_bank_account: v === "none" ? "" : v })} disabled={!isEditable}>
                  <SelectTrigger className="rounded-sm" data-testid="pf-bank-select"><SelectValue placeholder="Seleziona conto..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {bankAccounts.map((ba, idx) => (
                      <SelectItem key={`ba-${ba.iban}`} value={ba.iban}>{ba.bank_name} — {ba.iban}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card-surface p-5 space-y-3">
            <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Riepilogo</h2>
            <div className="space-y-2 text-sm">
              {!isLumpSum && <>
                {lineSubtotal > 0 && <div className="flex justify-between"><span className="text-zinc-500">Subtotale voci</span><span>{formatEUR(lineSubtotal)}</span></div>}
                {visibleMultiItems.map((mi, i) => (
                  <div key={`pf-mi-sum-${i}`} className="flex justify-between"><span className="text-zinc-500 truncate mr-2">{mi.description}</span><span className="shrink-0">{formatEUR(mi.price)}</span></div>
                ))}
                {extraLumpSum > 0 && (
                  <div className="flex justify-between" data-testid="pf-extra-lump-summary">
                    <span className="text-zinc-500 truncate mr-2">{form.extra_lump_sum_label || "Quotazione forfettaria"}</span>
                    <span className="shrink-0">{formatEUR(extraLumpSum)}</span>
                  </div>
                )}
              </>}
              {isLumpSum && <div className="flex justify-between"><span className="text-zinc-500">Trasloco (prezzo a corpo)</span><span>{formatEUR(subtotal)}</span></div>}
              {visibleExtraCosts.map((ec, i) => (
                <div key={`pf-ec-sum-${i}`} className="flex justify-between"><span className="text-zinc-500 truncate mr-2">{ec.description}</span><span className="shrink-0">{formatEUR(ec.amount)}</span></div>
              ))}
              {extraTotal > 0 && positiveExtraCount > 1 && <div className="flex justify-between text-zinc-400 text-xs"><span>Tot. costi extra</span><span>{formatEUR(extraTotal)}</span></div>}
              {/* Costo Movimentazione: campo informativo del preventivo, non mostrato nelle proforme */}
              {materialsCost > 0 && <div className="flex justify-between" data-testid="pf-materials-summary"><span className="text-zinc-500">Costo Materiali</span><span>{formatEUR(materialsCost)}</span></div>}
              {fractionalDeposit > 0 && <div className="flex justify-between" data-testid="pf-deposit-summary"><span className="text-zinc-500">Deposito{extDecimal && extDecimal > 0 ? ` (${extDecimal.toFixed(2)} mesi)` : ""}</span><span>{formatEUR(fractionalDeposit)}</span></div>}
              {releaseCost > 0 && <div className="flex justify-between" data-testid="pf-release-summary"><span className="text-zinc-500">Prezzo Riconsegna</span><span>{formatEUR(releaseCost)}</span></div>}
              <div className="flex justify-between items-center gap-2">
                <span className="text-zinc-500">IVA</span>
                <div className="flex items-center gap-1">
                  {vatZeroed ? (
                    <span
                      data-testid="pf-vat-zero-label"
                      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm mr-2"
                      title={VAT_MODE_SHORT[vatMode] || "Regime IVA speciale"}
                    >
                      {VAT_MODE_SHORT[vatMode] || "Regime speciale"} — 0%
                    </span>
                  ) : (
                    <>
                      <Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) || 0 })} disabled={!isEditable} className="rounded-sm w-16 h-7 text-xs" />
                      <span className="text-zinc-400">%</span>
                    </>
                  )}
                  <span className="ml-2" data-testid="pf-vat-amount">{formatEUR(vatAmount)}</span>
                </div>
              </div>
              {insuranceAmount > 0 && (
                <div className="flex justify-between" data-testid="pf-insurance-summary">
                  <span className="text-zinc-500">Assicurazione <span className="text-zinc-400 text-xs">(esente IVA)</span></span>
                  <span>{formatEUR(insuranceAmount)}</span>
                </div>
              )}
              {/* Totale ivato visibile solo se sconto > 0 */}
              {discountAmount > 0 && (
                <div className="flex justify-between text-xs italic text-zinc-500 border-t border-zinc-100 pt-1.5" data-testid="pf-total-with-vat-summary">
                  <span>Totale ivato</span><span>{formatEUR(totalWithVat)}</span>
                </div>
              )}
              {/* Sconto: editabile dall'admin, scalato dal totale ivato */}
              {canEditAll && isEditable ? (
                <div className="flex justify-between items-center gap-2" data-testid="pf-discount-row">
                  <span className="text-zinc-500">Sconto</span>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="0" step="0.01"
                      value={form.discount_amount || 0}
                      onChange={(e) => setForm({ ...form, discount_amount: parseFloat(e.target.value) || 0 })}
                      disabled={!isEditable}
                      className={`rounded-sm w-24 h-7 text-xs ${(form.discount_amount || 0) > totalWithVat ? "border-red-400 text-red-600" : ""}`}
                      placeholder="0,00"
                      data-testid="pf-discount-input"
                    />
                    <span className="text-zinc-400 text-xs">€</span>
                  </div>
                </div>
              ) : (
                discountAmount > 0 && <div className="flex justify-between text-emerald-700" data-testid="pf-discount-summary"><span>Sconto</span><span>− {formatEUR(discountAmount)}</span></div>
              )}
              <div className="border-t border-zinc-200 pt-2 flex justify-between font-semibold"><span>Totale</span><span className="text-[#002FA7]">{formatEUR(total)}</span></div>
              <div className="flex justify-between items-center gap-2"><span className="text-zinc-500">Caparra</span><Input type="number" placeholder="0 €" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: parseFloat(e.target.value) || 0 })} disabled={!isEditable} className="rounded-sm w-28 h-7 text-xs text-right" /></div>
              {(form.deposit || 0) > 0 && <div className="flex justify-between font-semibold text-emerald-700"><span>Saldo</span><span>{formatEUR(balanceDue)}</span></div>}
            </div>
          </div>

          {!isNew && (() => {
            const filtered = {
              inventories: relatedDocs.inventories,
              proformas: relatedDocs.proformas.filter(x => x.id !== id),
            };
            if (filtered.inventories.length === 0 && filtered.proformas.length === 0) return null;
            return (
              <div className="card-surface p-5 space-y-3" data-testid="related-docs-card">
                <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Documenti Collegati</h2>
                <RelatedDocsList relatedDocs={filtered} navigate={navigate} />
              </div>
            );
          })()}

          {pf?.versions && pf.versions.length > 1 && (
            <div className="card-surface p-5 space-y-3">
              <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Versioni</h2>
              <div className="space-y-2">
                {pf.versions.map(v => (
                  <button key={v.id} onClick={() => navigate(`/proformas/${v.id}`)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm border transition-colors ${v.id === id ? "border-[#002FA7] bg-blue-50/50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                    <span className="font-mono">{v.number || "BOZZA"}</span> <StatusBadge status={v.status} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      <PdfPreviewDialog
        pdfEndpoint={pdfPreviewUrl}
        open={!!pdfPreviewUrl}
        onClose={() => setPdfPreviewUrl(null)}
        filename={`${pf?.number || "proforma"}.pdf`}
      />

      {/* Pickup picker (≥2 inventari di ritiro eligibili) */}
      <PickupPickerDialog
        open={pickupPicker.open}
        pickups={pickupPicker.items}
        onClose={() => setPickupPicker({ open: false, items: [] })}
        onPick={async (pick) => {
          setPickupPicker({ open: false, items: [] });
          try {
            const { data } = await api.post(`/inventories/release-from-inventory/${pick.id}`);
            toast.success("Inventario di Riconsegna creato");
            navigate(`/inventories/${data.id}`);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        }}
      />
    </div>
  );
}


function PfMultiItemCard({ mi, miIdx, isEditable, hidePrice, updateMultiItem, removeMultiItem, addSubItem, removeSubItem, updateSubItem }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="border border-[#002FA7]/20 rounded-sm bg-blue-50/30" data-testid={`pf-multi-${miIdx}`}>
      <div className="flex flex-col sm:flex-row gap-2 p-3 sm:items-center">
        <button type="button" onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-white/60 rounded-sm shrink-0 self-start">
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        <Input placeholder="Descrizione gruppo" value={mi.description}
          onChange={(e) => updateMultiItem(miIdx, "description", e.target.value)}
          disabled={!isEditable} className="rounded-sm flex-1 font-medium bg-white" />
        <div className="flex items-center gap-2">
          {!hidePrice && <>
            <Input type="number" min="0" step="0.01" placeholder="Prezzo (€)" value={mi.price}
              onChange={(e) => updateMultiItem(miIdx, "price", parseFloat(e.target.value) || 0)}
              disabled={!isEditable} className="rounded-sm w-28 bg-white" />
            <span className="text-sm font-medium text-[#002FA7] w-24 text-right whitespace-nowrap">{formatEUR(mi.price || 0)}</span>
          </>}
          {isEditable && (
            <button onClick={() => removeMultiItem(miIdx)} className="p-2 hover:bg-red-50 rounded-sm">
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 ml-6 sm:ml-9 space-y-2 border-t border-[#002FA7]/10">
          <div className="flex items-center justify-between pt-2">
            <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Sotto-elementi ({(mi.sub_items || []).length})</span>
            {isEditable && (
              <button onClick={() => addSubItem(miIdx)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-0.5">
                <Plus className="w-3 h-3" /> Aggiungi
              </button>
            )}
          </div>
          {(mi.sub_items || []).map((si, siIdx) => (
            <div key={`pf-sub-${miIdx}-${siIdx}`} className="flex gap-2 items-center">
              <span className="text-xs text-zinc-300 w-5 shrink-0 text-right">{siIdx + 1}.</span>
              <Input placeholder="Descrizione elemento" value={si.description}
                onChange={(e) => updateSubItem(miIdx, siIdx, "description", e.target.value)}
                disabled={!isEditable} className="rounded-sm flex-1 h-8 text-sm bg-white" />
              <div className="flex items-center gap-1">
                <Input type="number" min="0" placeholder="Qtà" value={si.quantity}
                  onChange={(e) => updateSubItem(miIdx, siIdx, "quantity", parseInt(e.target.value) || 0)}
                  disabled={!isEditable} className="rounded-sm w-16 h-8 text-sm bg-white" />
                <span className="text-[10px] text-zinc-400">pz</span>
              </div>
              {isEditable && (mi.sub_items || []).length > 1 && (
                <button onClick={() => removeSubItem(miIdx, siIdx)} className="p-1 hover:bg-red-50 rounded-sm">
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
